"use client";

import { useState, useRef, useEffect } from "react";
import {
  Tooltip,
  TooltipTrigger,
  Button as AriaButton,
  TooltipProps,
} from "react-aria-components";

// Shared state to track the currently open tooltip
let currentOpenTooltip: string | null = null;
const listeners = new Set<(id: string | null) => void>();

const notifyListeners = (id: string | null): void => {
  currentOpenTooltip = id;
  listeners.forEach((listener) => listener(id));
};

interface IMyTooltipProps extends Omit<TooltipProps, "children"> {
  text: string;
  icon: React.ReactNode;
}

export function TooltipComponent({ text, icon }: IMyTooltipProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipId = useRef(
    `tooltip-${Math.random().toString(36).substr(2, 9)}`
  );

  useEffect(() => {
    const listener = (openId: string | null): void => {
      // Close this tooltip if another one is opened
      if (openId !== tooltipId.current && isOpen) {
        setIsOpen(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  const handleOpenChange = (open: boolean): void => {
    if (open) {
      // Close any other open tooltip
      notifyListeners(tooltipId.current);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set timeout to auto-close after 3 seconds
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        if (currentOpenTooltip === tooltipId.current) {
          notifyListeners(null);
        }
      }, 3000);
    } else {
      // Clear timeout when manually closed
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (currentOpenTooltip === tooltipId.current) {
        notifyListeners(null);
      }
    }
    setIsOpen(open);
  };

  const handleToggle = (): void => {
    handleOpenChange(!isOpen);
  };

  return (
    <TooltipTrigger isOpen={isOpen} onOpenChange={handleOpenChange} delay={0}>
      <AriaButton onPress={handleToggle}>{icon}</AriaButton>
      <Tooltip className="rounded bg-gray-900 px-2 py-1 text-white shadow-lg !text-sm focus:outline-0 max-w-[304px] mb-3">
        <div className="text-center">{text}</div>
        <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-900" />
      </Tooltip>
    </TooltipTrigger>
  );
}
